import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { ddm, ddmSv } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { Block, LoadError, Loading, MoreOptions, Pill, toast } from '../ui.js';
import { StaffReview } from './Review.js';
import { RoleIcon } from '../shell.js';

/**
 * The crew's own projects and dates on the personal link (owner 2026-10-18): freelancers and editors/camera use the Hub
 * from November while producers keep planning in Monday. Read only, and as little as possible by default: the project,
 * the next date, my videos. The rest (team, brief, links) is one click away.
 */

interface Stage {
  label: string;
  detail: string;
}
interface CrewVideo {
  id: string;
  name: string;
  stage: Stage;
  editor: string | null;
  firstDraftDue: string | null;
  nextDraftDue: string | null;
  internalDeadline: string | null;
  mine: boolean;
}
interface Contact {
  name: string;
  email: string;
  phone: string | null;
}
interface CrewProjectRow {
  id: string;
  name: string;
  client: string;
  deliveryMonth: string;
  stage: Stage;
  nextDeadline: string | null;
  myRoles: string[];
  producer: Contact | null;
  videos: CrewVideo[];
  fromMonday: boolean;
}
interface CrewShoot {
  id: string | null;
  date: string;
  start: string | null;
  end: string | null;
  location: string;
  plan: string;
  crew: { name: string; role: string; phone: string | null }[];
  onSite: { name: string; phone: string | null } | null;
  fromMonday: boolean;
  /** a Hub shoot on another day than Monday's shoot day */
  mondaySays?: string | null;
}
interface CrewProjectDetail extends CrewProjectRow {
  team: { name: string; role: string; key?: string; phone: string | null }[];
  brief: { label: string; text: string }[];
  shoots: CrewShoot[];
  links: { drive: string | null; clientDrive: string | null; brief: string | null; ziflow?: string | null };
  /** reviewed in Ziflow (Monday's "Ziflow" column; older API versions leave it out) */
  inZiflow?: boolean;
}
interface CrewReviews {
  on: boolean;
  videos: { id: string; latest: { id: string; number: number } | null; canSend: boolean }[];
}
/** A video's review, opened on the link (from the project or from the bell). */
export interface CrewReviewOpen {
  projectId: string;
  videoId: string;
  version: string | null;
}
interface CrewEvent {
  uid: string;
  date: string;
  start: string | null;
  end: string | null;
  kind: 'shoot' | 'first_draft' | 'next_draft' | 'internal_deadline';
  title: string;
  projectName: string;
  location: string;
}

const TX = texts(
  {
    projects: 'Your projects',
    none: 'You are not on any running project right now.',
    next: 'Next date',
    open: 'Show',
    close: 'Hide',
    mine: 'Yours',
    firstDraft: 'First draft',
    nextDraft: 'Next draft',
    internal: 'Internal deadline',
    producer: 'Producer',
    shoots: 'Shoots',
    mondayShoot: 'Shoot day from Monday – ask the producer for time and place.',
    mondaySays: (d: string) => `Monday says ${d} – check the day with the producer`,
    onSite: 'Contact on site',
    team: 'Team',
    brief: 'Brief',
    drive: 'Project folder in Drive',
    clientDrive: "Client's folder in Drive",
    ziflow: 'Review in Ziflow',
    briefDoc: 'Brief document',
    videos: 'Videos',
    dates: 'Coming dates',
    noDates: 'No dates in the coming months.',
    calendar: 'Add to your calendar',
    calendarInfo: 'Subscribe once and your shoots and draft dates show up in your own calendar (Google, Apple, Outlook) and stay up to date.',
    openCal: 'Open in my calendar app',
    copy: 'Copy address',
    copied: 'Address copied – paste it under "Add calendar from URL" in your calendar',
    google: 'In Google Calendar: Other calendars › + › From URL, paste the address.',
    renew: 'Make a new address (the old one stops working)',
    renewed: 'New address made – the old one no longer works',
    review: 'Review',
    sendVersion: 'Send version'
  },
  {
    projects: 'Dina projekt',
    none: 'Du är inte med i något pågående projekt just nu.',
    next: 'Nästa datum',
    open: 'Visa',
    close: 'Dölj',
    mine: 'Din',
    firstDraft: 'Första utkast',
    nextDraft: 'Nästa utkast',
    internal: 'Intern deadline',
    producer: 'Producent',
    shoots: 'Inspelningar',
    mondayShoot: 'Inspelningsdag från Monday – fråga producenten om tid och plats.',
    mondaySays: (d: string) => `Monday säger ${d} – kolla dagen med producenten`,
    onSite: 'Kontakt på plats',
    team: 'Team',
    brief: 'Brief',
    drive: 'Projektmappen i Drive',
    clientDrive: 'Kundens mapp i Drive',
    ziflow: 'Granskning i Ziflow',
    briefDoc: 'Briefdokument',
    videos: 'Videor',
    dates: 'Kommande datum',
    noDates: 'Inga datum de närmaste månaderna.',
    calendar: 'Lägg till i din kalender',
    calendarInfo: 'Prenumerera en gång så syns dina inspelningar och utkastdatum i din egen kalender (Google, Apple, Outlook) och hålls uppdaterade.',
    openCal: 'Öppna i min kalenderapp',
    copy: 'Kopiera adressen',
    copied: 'Adressen är kopierad – klistra in den under "Lägg till kalender via URL" i din kalender',
    google: 'I Google Kalender: Andra kalendrar › + › Via webbadress, klistra in adressen.',
    renew: 'Gör en ny adress (den gamla slutar fungera)',
    renewed: 'Ny adress klar – den gamla fungerar inte längre',
    review: 'Granskning',
    sendVersion: 'Skicka version'
  }
);

const tel = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

function Dates(p: { v: CrewVideo; d: (k: string) => string }) {
  const tx = TX[useLang()];
  const parts = [p.v.internalDeadline ? `${tx.internal} ${p.d(p.v.internalDeadline)}` : null, p.v.firstDraftDue ? `${tx.firstDraft} ${p.d(p.v.firstDraftDue)}` : null, p.v.nextDraftDue ? `${tx.nextDraft} ${p.d(p.v.nextDraftDue)}` : null].filter(Boolean);
  return parts.length ? <span class="small muted">{parts.join(' · ')}</span> : null;
}

/**
 * A project the person is on. With `tab` it is the whole page (owner 2026-10-20: freelancers get the same project page as
 * everyone, with its tabs Videos, Brief, Shoots and Files, minus what is not theirs: client contacts, prices, notes,
 * activity and the producer's controls). Without `tab` it is the folded row on the old link page.
 */
export function ProjectDetail(p: { api: Api; id: string; review: (o: CrewReviewOpen) => void; heading?: boolean }) {
  const lang = useLang();
  const tx = TX[lang];
  const d = lang === 'sv' ? ddmSv : ddm;
  const [v, setV] = useState<CrewProjectDetail | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [reviews, setReviews] = useState<CrewReviews | null>(null);
  useEffect(() => {
    p.api.get<CrewProjectDetail>(`/api/crew/projects/${p.id}`).then(setV, setErr);
    // the review buttons are extra: the project shows without them
    p.api.get<CrewReviews>(`/api/crew/projects/${p.id}/reviews`).then(setReviews, () => setReviews(null));
  }, [p.api, p.id]);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const videos = (
    <ul class="list">
      {v.videos.map((x) => {
        const r = reviews?.on ? reviews.videos.find((y) => y.id === x.id) : undefined;
        return (
          <li key={x.id} class="list-row">
            <span>
              <strong>{x.name}</strong> {x.mine ? <Pill tone="info">{tx.mine}</Pill> : null}
              <br />
              <span class="small">{x.stage.label}</span> <Dates v={x} d={d} />
            </span>
            {r && (r.latest || r.canSend) ? (
              <button type="button" class="btn btn-sm" onClick={() => p.review({ projectId: p.id, videoId: x.id, version: null })}>
                {r.latest ? tx.review : tx.sendVersion}
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
  const shoots = v.shoots.length ? (
    <ul class="list">
      {v.shoots.map((s) => (
        <li key={s.id ?? s.date}>
          <strong>
            {d(s.date)}
            {s.start && s.end ? ` ${s.start}–${s.end}` : ''}
          </strong>
          {s.location ? ` · ${s.location}` : ''}
          {s.fromMonday ? <p class="small muted">{tx.mondayShoot}</p> : null}
          {s.mondaySays ? <p class="small warn-text">{tx.mondaySays(d(s.mondaySays))}</p> : null}
          {s.plan ? <p class="small">{s.plan}</p> : null}
          {s.onSite ? (
            <p class="small">
              {tx.onSite}: {s.onSite.name}
              {s.onSite.phone ? (
                <>
                  {' · '}
                  <a href={tel(s.onSite.phone)}>{s.onSite.phone}</a>
                </>
              ) : null}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  ) : null;
  const producer = v.producer ? (
    <p class="small">
      {tx.producer}: <strong>{v.producer.name}</strong> · <a href={`mailto:${v.producer.email}`}>{v.producer.email}</a>
      {v.producer.phone ? (
        <>
          {' · '}
          <a href={tel(v.producer.phone)}>{v.producer.phone}</a>
        </>
      ) : null}
    </p>
  ) : null;
  const link = (href: string | null, label: string) =>
    href ? (
      <p class="small">
        <a href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      </p>
    ) : null;
  const brief = v.brief.length ? (
    <div>
      {v.brief.map((b) => (
        <p key={b.label} class="small">
          <strong>{b.label}:</strong> {b.text}
        </p>
      ))}
    </div>
  ) : null;
  const team = (
    <ul class="list small">
      {v.team.map((m) => (
        <li key={`${m.role}${m.name}`}>
          {m.key === 'editor' || m.key === 'camera' ? <RoleIcon role={m.key} label={m.role} /> : `${m.role}:`} {m.name}
          {m.phone ? (
            <>
              {' · '}
              <a href={tel(m.phone)}>{m.phone}</a>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
  return (
    <div class="stack">
      {/* opened on its own (from the board or "Idag" on the link), the project says which it is */}
      {p.heading ? (
        <header>
          <h1 class="h1">{v.name}</h1>
          <p class="muted">
            {v.client}
            {v.myRoles.length ? ` · ${v.myRoles.join(', ')}` : ''}
          </p>
        </header>
      ) : null}
      {videos}
      {shoots ? (
        <div>
          <p class="small-head">{tx.shoots}</p>
          {shoots}
        </div>
      ) : null}
      {producer}
      {/* a Ziflow project (owner 2026-10-24): its proofs are in Ziflow, so the folder is up front */}
      {v.inZiflow ? link(v.links.ziflow ?? null, tx.ziflow) : null}
      {link(v.links.drive, tx.drive)}
      <MoreOptions id="crew-project">
        {brief ? (
          <div>
            <p class="small-head">{tx.brief}</p>
            {brief}
          </div>
        ) : null}
        <p class="small-head">{tx.team}</p>
        {team}
        {link(v.links.clientDrive, tx.clientDrive)}
        {link(v.links.brief, tx.briefDoc)}
        {v.inZiflow ? null : link(v.links.ziflow ?? null, tx.ziflow)}
      </MoreOptions>
    </div>
  );
}

export function CalendarFeed(p: { api: Api }) {
  const tx = TX[useLang()];
  const [feed, setFeed] = useState<{ url: string; webcal: string } | null>(null);
  const make = async (renew: boolean) => {
    try {
      setFeed(await p.api.post<{ url: string; webcal: string }>('/api/crew/calendar-feed', {}));
      if (renew) toast({ text: tx.renewed });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  const copy = async () => {
    if (!feed) return;
    try {
      await navigator.clipboard.writeText(feed.url);
      toast({ text: tx.copied });
    } catch {
      /* no clipboard: the address is shown to copy by hand */
    }
  };
  return (
    <div class="stack">
      <p class="small muted">{tx.calendarInfo}</p>
      {feed ? (
        <>
          <p>
            <a class="btn btn-primary" href={feed.webcal}>
              {tx.openCal}
            </a>{' '}
            <button type="button" class="btn" onClick={() => void copy()}>
              {tx.copy}
            </button>
          </p>
          <p class="small muted" style={{ overflowWrap: 'anywhere' }}>
            {feed.url}
          </p>
          <p class="small muted">{tx.google}</p>
          <MoreOptions id="crew-calendar">
            <button type="button" class="btn" onClick={() => void make(true)}>
              {tx.renew}
            </button>
          </MoreOptions>
        </>
      ) : (
        <p>
          <button type="button" class="btn btn-primary" onClick={() => void make(false)}>
            {tx.calendar}
          </button>
        </p>
      )}
    </div>
  );
}

/** `review`/`openReview`: the link page holds the open review, so the bell can open one too. */
export function CrewPage(p: { api: Api; review?: CrewReviewOpen | null; openReview?: (o: CrewReviewOpen | null) => void }) {
  const lang = useLang();
  const tx = TX[lang];
  const d = lang === 'sv' ? ddmSv : ddm;
  const [rows, setRows] = useState<CrewProjectRow[] | null>(() => peek<{ projects: CrewProjectRow[] }>(p.api, '/api/crew/projects')?.projects ?? null);
  const [events, setEvents] = useState<CrewEvent[] | null>(() => peek<{ events: CrewEvent[] }>(p.api, '/api/crew/schedule')?.events ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [ownReview, setOwnReview] = useState<CrewReviewOpen | null>(null);
  const review = p.openReview ? (p.review ?? null) : ownReview;
  const openReview = p.openReview ?? setOwnReview;
  useEffect(() => {
    p.api.get<{ projects: CrewProjectRow[] }>('/api/crew/projects').then((r) => setRows(r.projects), setErr);
    p.api.get<{ events: CrewEvent[] }>('/api/crew/schedule').then((r) => setEvents(r.events), setErr);
  }, [p.api]);
  if (review)
    return (
      <section class="screen stack">
        <StaffReview key={`${review.projectId}:${review.videoId}`} api={p.api} projectId={review.projectId} videoId={review.videoId} version={review.version} crew={{ back: () => openReview(null) }} />
      </section>
    );
  if (err) return <LoadError error={err} />;
  if (!rows || !events) return <Loading />;
  return (
    <section class="screen screen--narrow stack">
      <Block title={tx.projects}>
        {rows.length ? (
          <ul class="list">
            {rows.map((r) => (
              <li key={r.id}>
                <div class="list-row">
                  <span>
                    <strong>{r.name}</strong> <span class="muted">· {r.client}</span>
                    <br />
                    <span class="small">
                      {r.myRoles.join(', ')}
                      {r.nextDeadline ? ` · ${tx.next} ${d(r.nextDeadline)}` : ''}
                    </span>
                  </span>
                  <button type="button" class="btn" aria-expanded={openId === r.id} onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                    {openId === r.id ? tx.close : tx.open}
                  </button>
                </div>
                {openId === r.id ? <ProjectDetail api={p.api} id={r.id} review={openReview} /> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p class="muted">{tx.none}</p>
        )}
      </Block>
      <Block title={tx.dates}>
        {events.length ? (
          <ul class="list">
            {events.slice(0, 12).map((e) => (
              <li key={e.uid} class="list-row">
                <span>
                  <strong>{d(e.date)}</strong>
                  {e.start ? ` ${e.start}` : ''} · {e.title}
                  {e.kind !== 'shoot' ? <span class="muted"> · {e.projectName}</span> : e.location ? <span class="muted"> · {e.location}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p class="muted">{tx.noDates}</p>
        )}
        <CalendarFeed api={p.api} />
      </Block>
    </section>
  );
}
